import { usePowerSync } from "@powersync/react"
import type { QueryResult } from "@powersync/common"
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react"
import {
  FAILED_RECORD_IDS_SQL,
  PENDING_RECORD_IDS_SQL,
} from "@/lib/powersync/uploadQueries"
import { useUploadMonitor } from "./uploadMonitorContext"
import { startUploadMonitor } from "@/lib/powersync/uploadMonitorWatches"

type Listener = () => void

/**
 * Sync status for a record relative to the server.
 * - 'pending': the record's row, or one of its photos (link row, photos row
 *   or file), is still waiting to upload
 * - 'failed': the server rejected the record or one of its photos; it is
 *   parked in sync_failures (or the photo upload was refused) and won't
 *   upload until someone retries it
 * - 'synced': everything about the record is on the server
 */
export type SyncStatus = "pending" | "failed" | "synced"

function setsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

/**
 * External store of record ids with pending uploads and with failures.
 *
 * Consumers subscribe through `useSyncExternalStore` with a per-record
 * snapshot (`status`), so when the queue changes every listener re-reads only
 * its own id's status. Rows whose own status is unchanged bail out of the
 * re-render — a queue change costs O(1) React work per row, not a full
 * re-render of every visible row.
 */
export class RecordSyncStatusStore {
  private pendingIds = new Set<string>()
  private failedIds = new Set<string>()
  private listeners = new Set<Listener>()

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * A failure outranks pending: a rejected record is never shown as merely
   * waiting (or, once its queue entry is gone, as synced).
   */
  status(id: string): SyncStatus {
    if (this.failedIds.has(id)) return "failed"
    if (this.pendingIds.has(id)) return "pending"
    return "synced"
  }

  /**
   * Replace the pending set. Listeners are notified only when membership
   * actually changed, so no-op queue activity (e.g. an extra PATCH on an
   * already-pending record) re-renders nothing.
   */
  commitPending(next: ReadonlySet<string>): void {
    if (setsEqual(this.pendingIds, next)) return
    this.pendingIds = new Set(next)
    this.notify()
  }

  /** Replace the failed set, notifying only on a membership change. */
  commitFailed(next: ReadonlySet<string>): void {
    if (setsEqual(this.failedIds, next)) return
    this.failedIds = new Set(next)
    this.notify()
  }

  private notify() {
    for (const listener of this.listeners) {
      listener()
    }
  }
}

const StoreContext = createContext<RecordSyncStatusStore | null>(null)

/**
 * The shared record status store, or null when rendered outside of
 * `SyncStatusProvider` (hooks then report everything as synced).
 */
export function useSyncStatusStore(): RecordSyncStatusStore | null {
  return useContext(StoreContext)
}

/**
 * Coalesce bursts (a batch completing, photo files downloading); a tick that
 * turns green a moment later is fine.
 */
const RECORD_STATUS_THROTTLE_MS = 250

/**
 * The tables whose changes can change a record's status. The queries also
 * read the photo link tables, but a link row written on this device always
 * comes with a ps_crud entry (and a photo file with an attachments change),
 * so leaving them out stops every downloaded teammate's photo link from
 * re-running these.
 */
const PENDING_TABLES = ["ps_crud", "attachments", "attachment_failures"]
const FAILED_TABLES = ["sync_failures", "attachments", "attachment_failures"]

function idsOf(result: QueryResult): Set<string> {
  const ids = new Set<string>()
  const rows = result.array ?? result.rows?._array ?? []
  for (const row of rows) {
    const id = (row as { id?: unknown }).id
    if (typeof id === "string") ids.add(id)
  }
  return ids
}

/**
 * App-wide source of truth for what is waiting to upload.
 *
 * Two shared `db.watch`es (pending and failed record ids) replace the
 * per-row `statusChanged` listeners + per-row `LIKE` queries that used to
 * run a full queue scan once per visible row per status tick. PowerSync's
 * own upload pipeline detects new `ps_crud` entries through the same
 * table-update notifications that drive `db.watch`, so these fire when the
 * queue, the photo files or the failures actually change — not on every
 * upload/download progress flag toggle.
 *
 * Also starts the upload monitor's watches, which the project upload status
 * reads (see `useProjectUploadStatus`).
 */
export function SyncStatusProvider({ children }: { children: ReactNode }) {
  const powerSync = usePowerSync()
  const monitor = useUploadMonitor()
  const storeRef = useRef<RecordSyncStatusStore | null>(null)
  if (storeRef.current === null) {
    storeRef.current = new RecordSyncStatusStore()
  }

  useEffect(() => {
    const controller = new AbortController()
    const store = storeRef.current

    powerSync.watch(
      PENDING_RECORD_IDS_SQL,
      [],
      {
        onResult: (result) => store?.commitPending(idsOf(result)),
        onError: (error) => {
          console.error("[SyncStatus] pending ids watch failed:", error)
        },
      },
      {
        signal: controller.signal,
        tables: PENDING_TABLES,
        throttleMs: RECORD_STATUS_THROTTLE_MS,
      },
    )

    powerSync.watch(
      FAILED_RECORD_IDS_SQL,
      [],
      {
        onResult: (result) => store?.commitFailed(idsOf(result)),
        onError: (error) => {
          console.error("[SyncStatus] failed ids watch failed:", error)
        },
      },
      {
        signal: controller.signal,
        tables: FAILED_TABLES,
        throttleMs: RECORD_STATUS_THROTTLE_MS,
      },
    )

    return () => controller.abort()
  }, [powerSync])

  useEffect(() => startUploadMonitor(powerSync, monitor), [powerSync, monitor])

  return (
    <StoreContext.Provider value={storeRef.current}>
      {children}
    </StoreContext.Provider>
  )
}
